<template>
  <div class="col-12 noprint">
    <h3 class="font-light mb-2">Verificación en dos pasos (2FA)</h3>

    <!-- Estado cargando -->
    <div v-if="loadingStatus" class="text-sm text-500">Verificando estado...</div>

    <!-- 2FA deshabilitado -->
    <div v-if="!loadingStatus && !totpEnabled && step === null">
      <p class="text-sm mb-3">
        El 2FA no está activado. Activarlo agrega una capa de seguridad extra:
        al iniciar sesión necesitarás ingresar un código de tu aplicación de autenticación
        (Google Authenticator, Authy, etc.).
      </p>
      <Button label="Activar 2FA" icon="pi pi-shield" @click="setupTotp" :loading="loading" />
    </div>

    <!-- 2FA habilitado -->
    <div v-if="!loadingStatus && totpEnabled && step === null">
      <div class="flex align-items-center gap-2 mb-3">
        <i class="pi pi-check-circle text-green-500 text-xl"></i>
        <span class="font-bold text-green-600">2FA activado</span>
      </div>
      <p class="text-sm mb-3">
        Tu cuenta está protegida con verificación en dos pasos.
      </p>
      <Button label="Desactivar 2FA" icon="pi pi-times" severity="danger" outlined @click="step = 'disable'" />
    </div>

    <!-- Paso: escanear QR -->
    <div v-if="step === 'setup'">
      <p class="text-sm mb-3">
        Escanea este código QR con tu aplicación de autenticación (Google Authenticator, Authy, etc.)
        y luego ingresa el código de 6 dígitos para confirmar.
      </p>
      <div v-if="qrCodeSvg" class="mb-3 bg-white p-2 inline-block" v-html="qrCodeSvg"></div>
      <div class="text-xs mb-3 text-500">
        ¿No podés escanear el QR? Clave manual:
        <code class="font-bold">{{ secret }}</code>
      </div>
      <div class="form flex flex-column gap-2 mt-3" style="max-width: 280px">
        <label>Código de confirmación</label>
        <InputText v-model="code" placeholder="000000" maxlength="6" @keyup.enter="confirmTotp" />
        <div v-if="error" class="text-red-500 text-sm">{{ error }}</div>
        <div class="flex gap-2">
          <Button label="Confirmar" @click="confirmTotp" :loading="loading" />
          <Button label="Cancelar" severity="secondary" @click="cancelSetup" />
        </div>
      </div>
    </div>

    <!-- Paso: deshabilitar -->
    <div v-if="step === 'disable'">
      <p class="text-sm mb-3">
        Ingresa el código de tu aplicación de autenticación para desactivar el 2FA.
      </p>
      <div class="form flex flex-column gap-2" style="max-width: 280px">
        <label>Código de autenticación</label>
        <InputText v-model="code" placeholder="000000" maxlength="6" @keyup.enter="disableTotp" />
        <div v-if="error" class="text-red-500 text-sm">{{ error }}</div>
        <div class="flex gap-2">
          <Button label="Desactivar" severity="danger" @click="disableTotp" :loading="loading" />
          <Button label="Cancelar" severity="secondary" @click="step = null; code = ''; error = null" />
        </div>
      </div>
    </div>

    <!-- Éxito -->
    <div v-if="step === 'success'" class="flex align-items-center gap-2 mt-2">
      <i class="pi pi-check-circle text-green-500 text-xl"></i>
      <span class="text-green-600 font-bold">{{ successMessage }}</span>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useApi } from '@/composables/useApi'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'

const api = useApi()

const totpEnabled = ref(false)
const loadingStatus = ref(true)
const loading = ref(false)
const step = ref(null) // null | 'setup' | 'disable' | 'success'
const qrCodeSvg = ref(null)
const secret = ref(null)
const code = ref('')
const error = ref(null)
const successMessage = ref('')

const loadStatus = async () => {
  loadingStatus.value = true
  try {
    const result = await api.get('/api/auth/totp/status')
    totpEnabled.value = result.totpEnabled
  } catch {
    // ignore
  } finally {
    loadingStatus.value = false
  }
}

const setupTotp = async () => {
  loading.value = true
  error.value = null
  try {
    const result = await api.get('/api/auth/totp/setup')
    if (result.error) { error.value = result.error; return }
    qrCodeSvg.value = result.qrCodeSvg
    secret.value = result.secret
    step.value = 'setup'
  } catch {
    error.value = 'Error al iniciar la configuración'
  } finally {
    loading.value = false
  }
}

const confirmTotp = async () => {
  if (!code.value) return
  loading.value = true
  error.value = null
  try {
    const result = await api.post('/api/auth/totp/confirm', { code: code.value })
    if (result.error) { error.value = result.error; return }
    totpEnabled.value = true
    step.value = 'success'
    successMessage.value = '¡2FA activado correctamente!'
    setTimeout(() => { step.value = null }, 3000)
  } catch {
    error.value = 'Error al confirmar el código'
  } finally {
    loading.value = false
  }
}

const disableTotp = async () => {
  if (!code.value) return
  loading.value = true
  error.value = null
  try {
    const result = await api.post('/api/auth/totp/disable', { code: code.value })
    if (result.error) { error.value = result.error; return }
    totpEnabled.value = false
    step.value = 'success'
    successMessage.value = '2FA desactivado.'
    setTimeout(() => { step.value = null }, 3000)
  } catch {
    error.value = 'Error al desactivar el 2FA'
  } finally {
    loading.value = false
  }
}

const cancelSetup = () => {
  step.value = null
  code.value = ''
  error.value = null
  qrCodeSvg.value = null
  secret.value = null
}

onMounted(loadStatus)
</script>
